import React, { useState, useEffect } from 'react';
import { profileIconAPI } from '../../services/api';
import { LockClosedIcon, CheckIcon } from '@heroicons/react/24/solid';

interface ProfileIcon {
  id: string;
  icon_key: string;
  icon_url: string;
  name: string;
  rarity: string;
  unlock_type: string;
  unlock_requirement?: any;
  is_unlocked: boolean;
  is_selected: boolean;
  sort_order: number;
}

const IconSelector: React.FC = () => {
  const [icons, setIcons] = useState<ProfileIcon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIconId, setSelectedIconId] = useState<string | null>(null);

  useEffect(() => {
    loadIcons();
  }, []);

  const loadIcons = async () => {
    try {
      setLoading(true);
      const data = await profileIconAPI.getAvailableIcons();
      setIcons(data);
      const selected = data.find((icon: ProfileIcon) => icon.is_selected);
      if (selected) {
        setSelectedIconId(selected.id);
      }
    } catch (err) {
      console.error('Failed to load icons:', err);
      setError('Failed to load profile icons');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectIcon = async (icon: ProfileIcon) => {
    if (!icon.is_unlocked) return;

    try {
      await profileIconAPI.selectIcon(icon.id);
      setSelectedIconId(icon.id);
      // Update local state
      setIcons(icons.map(i => ({
        ...i,
        is_selected: i.id === icon.id
      })));
      // Optionally trigger a re-render of the user menu or other components
      window.location.reload(); // Simple way to update everywhere
    } catch (err) {
      console.error('Failed to select icon:', err);
      alert('Failed to select icon. Please try again.');
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      case 'epic':
        return 'border-purple-400 bg-purple-50 dark:bg-purple-900/20';
      case 'rare':
        return 'border-blue-400 bg-blue-50 dark:bg-blue-900/20';
      default:
        return 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800';
    }
  };

  const getRarityBadgeColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'bg-yellow-500 text-white';
      case 'epic':
        return 'bg-purple-500 text-white';
      case 'rare':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const groupIconsByRarity = () => {
    const grouped: Record<string, ProfileIcon[]> = {
      common: [],
      rare: [],
      epic: [],
      legendary: []
    };

    icons.forEach(icon => {
      grouped[icon.rarity]?.push(icon);
    });

    return grouped;
  };

  const getUnlockRequirementText = (icon: ProfileIcon) => {
    if (!icon.unlock_requirement) return null;

    if (icon.unlock_type === 'rating_milestone') {
      return `Reach rating ${icon.unlock_requirement.rating}`;
    }

    if (icon.unlock_type === 'achievement') {
      return 'Complete achievement';
    }

    return 'Special unlock';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mango-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  const groupedIcons = groupIconsByRarity();

  return (
    <div className="space-y-6">
      {Object.entries(groupedIcons).map(([rarity, rarityIcons]) => {
        if (rarityIcons.length === 0) return null;

        return (
          <div key={rarity}>
            <div className="flex items-center space-x-2 mb-3">
              <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${getRarityBadgeColor(rarity)}`}>
                {rarity.toUpperCase()}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {rarityIcons.filter(i => i.is_unlocked).length} / {rarityIcons.length} unlocked
              </span>
            </div>

            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
              {rarityIcons.map(icon => (
                <button
                  key={icon.id}
                  onClick={() => handleSelectIcon(icon)}
                  disabled={!icon.is_unlocked}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    getRarityColor(rarity)
                  } ${
                    icon.is_unlocked
                      ? 'cursor-pointer hover:scale-110 hover:shadow-lg'
                      : 'opacity-40 cursor-not-allowed'
                  } ${
                    selectedIconId === icon.id
                      ? 'ring-4 ring-mango-500 scale-105'
                      : ''
                  }`}
                  title={icon.name}
                >
                  <div className="text-3xl text-center">
                    {icon.icon_url}
                  </div>

                  {/* Selected checkmark */}
                  {selectedIconId === icon.id && (
                    <div className="absolute top-1 right-1 bg-mango-500 rounded-full p-0.5">
                      <CheckIcon className="h-3 w-3 text-white" />
                    </div>
                  )}

                  {/* Locked overlay */}
                  {!icon.is_unlocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                      <LockClosedIcon className="h-6 w-6 text-white" />
                    </div>
                  )}

                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {icon.name}
                    {!icon.is_unlocked && icon.unlock_requirement && (
                      <div className="text-yellow-300 text-xs">
                        {getUnlockRequirementText(icon)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default IconSelector;
